import { Box, Group, Skeleton, Text, UnstyledButton } from "@mantine/core";
import { IconAlertTriangle, IconSparkles } from "@tabler/icons-react";

import styles from "./NarrativeAIState.module.css";

/**
 * The four AI lifecycle states of the narrative surface (openspec long-form-ai, task 1.20).
 * NOTE: no Figma exists for these yet — built to the existing card chrome, pending design.
 */
export type AIState = "generating" | "awaiting-cues" | "failed" | "new-script";

export interface NarrativeAIStateProps {
    state: AIState;
    /** new-script: load the replacement narrative (preserves read/skip state). */
    onLoadNewScript?: () => void;
    /**
     * generating: reveal the approved fallback script instead of waiting. Omitted when there is no
     * fallback to show, which is why the button is conditional rather than always rendered disabled.
     */
    onShowFallback?: () => void;
}

export const NarrativeAIState = ({ state, onLoadNewScript, onShowFallback }: NarrativeAIStateProps) => {
    if (state === "new-script") {
        return (
            <Group justify="space-between" align="center" gap={12} className={styles.banner}>
                <Group component="span" gap={8} align="center">
                    <span className={styles.bannerDot} />
                    <span className={styles.bannerText}>New script available</span>
                </Group>
                <UnstyledButton className={styles.loadBtn} onClick={onLoadNewScript}>
                    Show latest
                </UnstyledButton>
            </Group>
        );
    }

    if (state === "generating") {
        return (
            <Box className={styles.card}>
                <Group gap={8} align="center" mb={12}>
                    <IconSparkles size={16} className={styles.sparkle} />
                    <span className={styles.headText}>Waiting on sales narrative</span>
                </Group>
                <Skeleton height={10} radius="sm" mb={8} />
                <Skeleton height={10} radius="sm" mb={8} width="92%" />
                <Skeleton height={10} radius="sm" width="68%" />
                {onShowFallback && (
                    <UnstyledButton className={styles.fallbackBtn} onClick={onShowFallback} mt={12}>
                        Use the standard script
                    </UnstyledButton>
                )}
            </Box>
        );
    }

    if (state === "awaiting-cues") {
        return (
            <Box className={styles.card}>
                <Group gap={8} align="center" mb={12}>
                    <IconSparkles size={16} className={styles.sparkle} />
                    <span className={styles.headText}>Listening for customer cues</span>
                </Group>
                <Text component="p" my={0} className={styles.sub}>
                    Your script personalizes as we pick up the customer&apos;s devices and needs.
                </Text>
            </Box>
        );
    }

    return (
        <Box className={styles.card}>
            <Group gap={8} align="center" mb={12}>
                <IconAlertTriangle size={16} className={styles.failIcon} />
                <span className={styles.headText}>Script unavailable</span>
            </Group>
            <Text component="p" my={0} className={styles.sub}>
                We couldn&apos;t load a personalized script for this offer.
            </Text>
        </Box>
    );
};
